/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from "react";
import svgPaths from "../imports/svg-cwxh2va60v";
import { useAuth } from "../contexts/AuthContext";

type ScheduleItem = {
  id: string;
  startTime: string;
  endTime: string;
  status: string;
  isActive: boolean;
  show: any;
};

export default function Hero({currentPlaying, setPlaying} : {currentPlaying?: boolean, setPlaying?: any}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const { siteData: data, landingDataLoading, landingPageData } = useAuth()
  const [liveSchedule, setLiveSchedule] = useState<any>(null)

  function getSingleLiveSchedule(
    scheduleItems: ScheduleItem[]
  ): ScheduleItem | null {
    const liveSchedules = scheduleItems
      .filter(
        (item) => item.isActive && item.status === 'live'
      )
      .sort(
        (a, b) =>
          new Date(a.startTime).getTime() -
          new Date(b.startTime).getTime()
      );

    return liveSchedules.length > 0 ? liveSchedules[0] : null;
  }

  useEffect(() => {
    if (!currentPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false)
    }
  }, [currentPlaying]);

  useEffect(()=>{
    if(landingPageData){
      const data = getSingleLiveSchedule(landingPageData?.scheduleItems)
      setLiveSchedule(data)
    }
  },[landingPageData])

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setPlaying(null);
      
    } else {
      audioRef.current.play();
      setPlaying("live_song");
    }

    setIsPlaying(!isPlaying);
  };

  const [heights, setHeights] = useState<number[]>([]);

  useEffect(() => {
    if (isPlaying) {
      const interval = setInterval(() => {
        setHeights(
          Array(7)
            .fill(0)
            .map(() => Math.max(30, Math.random() * 100))
        );
      }, 300); 
      return () => clearInterval(interval);
    } else {
      setHeights(Array(7).fill(30));
    }
  }, [isPlaying]);

  return (
    <section className="relative min-h-screen w-full overflow-hidden flex flex-col items-center justify-center pt-34 pb-20 px-4">
      <div className="absolute inset-0 bg-linear-to-t from-[#000000] to-[#55afba] -z-20" />

      {/* 🎵 AUDIO TAG */}
      <audio
        ref={audioRef}
        src={ data ? data?.radioLink : `https://globalwebmusic.com/akaram.mp3`}
        preload="none"
      />

      <div className="max-w-4xl w-full flex flex-col items-center text-center z-10">
        {/* Live Badge */}
        <div className="bg-red-500/20 border border-red-500/50 rounded-full px-4 py-1 flex items-center gap-2 mb-6 backdrop-blur-sm">
          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <span className="text-red-500 font-medium text-sm tracking-wider">
            LIVE NOW
          </span>
        </div>

        {/* Heading */}
        <h1 className="text-5xl md:text-7xl font-medium text-white mb-6 tracking-tight leading-tight">
          The Sound of <br className="hidden sm:block" /> Your City
        </h1>

        <p className="text-white/80 text-lg md:text-xl max-w-2xl mb-12 leading-relaxed">
          Broadcasting 24/7 with the best mix of music, talk shows, and entertainment.
        </p>

        {/* Player Card */}
        <div className="w-full max-w-2xl bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-6 flex items-center justify-between mb-8 group hover:bg-white/10 transition-all duration-300">
          <div className="flex items-center gap-6">
            {/* ▶ PLAY BUTTON */}
            <button
              onClick={togglePlay}
              className="w-14 h-14 bg-white rounded-full flex items-center justify-center shrink-0 hover:scale-105 transition-transform shadow-lg shadow-cyan-900/20"
            >
              {isPlaying ? (
                <div className="flex gap-1">
                  <span className="w-1.5 h-5 bg-black rounded" />
                  <span className="w-1.5 h-5 bg-black rounded" />
                </div>
              ) : (
                <svg className="w-4 h-4 fill-black ml-1" viewBox="0 0 16 16">
                  <path d={svgPaths.p2bb5f600} />
                </svg>
              )}
            </button>

            <div className="text-left">
              <p className="text-cyan-100 text-xs font-medium tracking-widest uppercase mb-1">
                Now Playing
              </p>
              {
                landingDataLoading ?  <div className="h-5 w-48 bg-white/20 rounded animate-pulse mb-2" /> :
                <h3 className="text-white text-lg font-medium mb-0.5">
                  {liveSchedule ? liveSchedule?.show?.name : "The Morning Show"}
                </h3>
              }
              {
                landingDataLoading ? <div className="h-4 w-32 bg-white/15 rounded animate-pulse" /> :
                <p className="text-white/70 text-sm">{liveSchedule ? liveSchedule?.show?.hostName : "Sarah Johnson"}</p>
              }
            </div>
          </div>

          {/* Visualizer */}
          <div className="flex items-end gap-1 h-8">
            {Array(7)
              .fill(0)
              .map((_, i) => (
                <div
                  key={i}
                  className="w-1 bg-white rounded-full transition-all duration-300"
                  style={{
                    height: `${heights[i] || 30}%`,
                    opacity: 0.5 + (i % 3) * 0.2,
                  }}
                />
              ))}
          </div>
        </div>

        {/* Actions */}
        {/* <div className="flex items-center gap-4">
          <button className="bg-white text-black px-6 py-3 rounded-lg font-medium hover:bg-cyan-50 transition-colors">
            View Schedule
          </button>
          <button className="border border-white/30 text-white px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors">
            About Us
          </button>
        </div> */}
      </div>
    </section>
  );
}
